import type { ReduxAction } from "actions/ReduxActionTypes";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";

export interface AIMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

export interface UpdateAISettingsPayload {
  provider?: string;
  hasApiKey?: boolean;
  isEnabled?: boolean;
}

export const updateAISettings = (
  payload: UpdateAISettingsPayload,
): ReduxAction<UpdateAISettingsPayload> => ({
  type: ReduxActionTypes.UPDATE_AI_SETTINGS,
  payload,
});

export interface FetchAIResponsePayload {
  prompt: string;
  context?: {
    functionName?: string;
    cursorLineNumber?: number;
    functionString?: string;
    mode?: string;
    currentValue?: string;
    /** Unpublished action id for server-side datasource schema resolution */
    entityId?: string;
    databaseSchema?: string;
    datasourceType?: string;
  };
}

export const fetchAIResponse = (
  payload: FetchAIResponsePayload,
): ReduxAction<FetchAIResponsePayload> => ({
  type: ReduxActionTypes.FETCH_AI_RESPONSE,
  payload,
});

export const fetchAIResponseSuccess = (payload: {
  response: string;
}): ReduxAction<{ response: string }> => ({
  type: ReduxActionTypes.FETCH_AI_RESPONSE_SUCCESS,
  payload,
});

export const fetchAIResponseError = (payload: {
  error: string;
}): ReduxAction<{ error: string }> => ({
  type: ReduxActionTypes.FETCH_AI_RESPONSE_ERROR,
  payload,
});

export const loadAISettings = (): ReduxAction<undefined> => ({
  type: ReduxActionTypes.LOAD_AI_SETTINGS,
  payload: undefined,
});

export const clearAIResponse = (): ReduxAction<undefined> => ({
  type: ReduxActionTypes.CLEAR_AI_RESPONSE,
  payload: undefined,
});

export const cancelAIResponse = (): ReduxAction<undefined> => ({
  type: ReduxActionTypes.CANCEL_AI_RESPONSE,
  payload: undefined,
});

export const openAIPanel = (): ReduxAction<undefined> => ({
  type: ReduxActionTypes.OPEN_AI_PANEL,
  payload: undefined,
});

export const closeAIPanel = (): ReduxAction<undefined> => ({
  type: ReduxActionTypes.CLOSE_AI_PANEL,
  payload: undefined,
});

export interface AIEditorContextPayload {
  functionName?: string;
  cursorLineNumber?: number;
  functionString?: string;
  mode?: string;
  currentValue?: string;
  editorId?: string;
  entityName?: string;
  /** Id of the entity the editor belongs to — an action id, widget id, etc. depending on entityType */
  entityId?: string;
  /** ENTITY_TYPE of the entity; only an ACTION's id is sent to the server as entityId */
  entityType?: string;
  propertyPath?: string;
}

export const updateAIContext = (
  context: AIEditorContextPayload,
): ReduxAction<{ context: AIEditorContextPayload }> => ({
  type: ReduxActionTypes.UPDATE_AI_CONTEXT,
  payload: { context },
});

// Combined action: updates context and opens panel
export const openAIPanelWithContext = (
  context: AIEditorContextPayload,
): ReduxAction<{ context: AIEditorContextPayload }> => ({
  type: ReduxActionTypes.OPEN_AI_PANEL_WITH_CONTEXT,
  payload: { context },
});
